/**
 * Planned work types + per-type quantities for pipeline sections (SOW).
 * API: `plannedWorkItems`: [{ workType, quantity }]; legacy `plannedWorks` / `plannedWelds` kept for roll-ups.
 */

export const PLANNED_WORK_TYPE_OPTIONS = [
  { value: 'Components', label: 'Components' },
  { value: 'Days', label: 'Days' },
  { value: 'Field Joints', label: 'Field Joints' },
  { value: 'Welds', label: 'Welds' },
  { value: 'Coating', label: 'Coating' },
  { value: 'Weld', label: 'Weld' },
];

const WELD_LIKE_FOR_ROLLUP = new Set(['Welds', 'Weld', 'Field Joints']);

function toNullableNumber(v) {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).trim());
  return Number.isFinite(n) ? n : null;
}

/** UI rows: { id, workType, quantity } */
export function parsePlannedWorkItemsFromRecord(record) {
  const raw = record?.plannedWorkItems ?? record?.planned_work_items;
  if (Array.isArray(raw) && raw.length) {
    return raw.map((it, idx) => ({
      id: `pwi-${idx}-${it.workType ?? it.work_type ?? idx}`,
      workType: String(it.workType ?? it.work_type ?? '').trim(),
      quantity:
        it.quantity != null && it.quantity !== '' ? String(it.quantity) : '',
    })).filter((x) => x.workType);
  }
  const legacyWork = record?.plannedWorks ?? record?.planned_works ?? '';
  const legacyQty = record?.plannedWelds ?? record?.planned_welds;
  if (legacyWork != null && String(legacyWork).trim() !== '') {
    return [
      {
        id: 'legacy',
        workType: String(legacyWork).trim(),
        quantity:
          legacyQty != null && legacyQty !== '' ? String(legacyQty) : '',
      },
    ];
  }
  return [];
}

export function formatPlannedWorkSummary(recordOrItems) {
  const items = Array.isArray(recordOrItems)
    ? recordOrItems
    : parsePlannedWorkItemsFromRecord(recordOrItems);
  if (!items.length) return '—';
  return items
    .map((it) => {
      const wt = it.workType ?? '';
      const q =
        it.quantity != null && String(it.quantity).trim() !== ''
          ? String(it.quantity).trim()
          : '—';
      return `${wt}: ${q}`;
    })
    .join('; ');
}

export function rollupWeldsQuantityFromItems(items) {
  let sum = 0;
  let any = false;
  (Array.isArray(items) ? items : []).forEach((it) => {
    const wt = it.workType ?? it.work_type;
    if (!WELD_LIKE_FOR_ROLLUP.has(wt)) return;
    const q = toNullableNumber(it.quantity ?? it.qty);
    if (q != null) {
      sum += q;
      any = true;
    }
  });
  return any ? sum : null;
}

/** Single-cell rollup for tables (weld-like quantities only). */
export function displayPlannedQtySummary(record) {
  const items = record?.plannedWorkItems ?? record?.planned_work_items;
  if (Array.isArray(items) && items.length > 0) {
    const rolled = rollupWeldsQuantityFromItems(items);
    if (rolled != null) return String(rolled);
    return '—';
  }
  if (record?.plannedWelds != null && record?.plannedWelds !== '')
    return String(record.plannedWelds);
  return '—';
}

/** Fields to merge into add/update SOW payload. */
export function buildPlannedWorkApiFields(uiRows) {
  const cleaned = (Array.isArray(uiRows) ? uiRows : [])
    .filter((it) => it.workType && String(it.workType).trim())
    .map((it) => ({
      workType: String(it.workType).trim(),
      quantity: toNullableNumber(it.quantity),
    }));
  const rollup = rollupWeldsQuantityFromItems(cleaned);
  const legacyWorks = cleaned.map((c) => c.workType).filter(Boolean).join(', ');
  return {
    ...(cleaned.length ? { plannedWorkItems: cleaned } : {}),
    ...(legacyWorks ? { plannedWorks: legacyWorks } : {}),
    ...(rollup != null ? { plannedWelds: rollup } : {}),
  };
}
